import { ICellRendererParams, RowNode } from '@ag-grid-community/core';
import { AsyncPipe } from '@angular/common';
import { Component, OnDestroy } from '@angular/core';
import { CdwCheckboxModule } from '@cdw-ae/common-ui-components-checkbox';
import { combineLatest, Observable, Subject, take, takeUntil } from 'rxjs';

import { CdwSearchGridComponentStore } from '../+component-state/cdw-search-grid-component.store';

@Component({
  selector: 'lib-multi-select-header',
  templateUrl: './multi-select-header.component.html',
  styleUrls: ['./multi-select-header.component.scss'],
  standalone: true,
  imports: [AsyncPipe, CdwCheckboxModule],
})
export class MultiSelectHeaderComponent<T extends { id: string }>
  implements OnDestroy
{
  public params!: ICellRendererParams<T>;

  public indeterminate = false;

  public checked$: Observable<boolean>;

  public indeterminate$: Observable<boolean>;

  public promptSelectAll$: Observable<boolean>;

  private onDestroy$: Subject<void> = new Subject();

  private entitiesExceedPageSize!: boolean;

  constructor(private readonly componentState: CdwSearchGridComponentStore<T>) {
    this.checked$ = this.componentState.multiSelectCheckboxChecked$;
    this.indeterminate$ = this.componentState.multiSelectCheckboxIndeterminate$;
    this.promptSelectAll$ = this.componentState.promptSelectAll$;

    this.componentState.entitiesExceedPageSize$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((entitiesExceedPageSize: boolean): void => {
        this.entitiesExceedPageSize = entitiesExceedPageSize;
      });
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next();
  }

  public agInit(params: ICellRendererParams<T>): void {
    this.params = params;

    this.params.api.addEventListener('filterChanged', (): void => {
      // Note: AG Grid filtering has not been implemented yet
      this.updateDeviceIdsVisibleOnPage();
    });

    this.params.api.addEventListener('paginationChanged', (): void => {
      this.updateDeviceIdsVisibleOnPage();
    });

    this.updateDeviceIdsVisibleOnPage();
  }

  public clickMultiSelectHeaderCheckbox($event: MouseEvent): void {
    $event.stopPropagation();

    combineLatest([this.checked$, this.indeterminate$])
      .pipe(take(1))
      .subscribe(([checked, indeterminate]: [boolean, boolean]): void => {
        if (checked || indeterminate) {
          this.deselectAllOnPage();
          return;
        }
        this.selectAllOnPage();
      });
  }

  private selectAllOnPage(): void {
    if (this.entitiesExceedPageSize) {
      this.componentState.turnOnPromptSelectAll();
    }
    const ids: (string | number)[] = this.getDisplayedIds();
    this.componentState.setSelectedIds({ ids });
    this.componentState.updateMultiSelectCheckboxState();
    // this.promptSelectAll$
    //   .pipe(
    //     filter((promptSelectAll: boolean): boolean => promptSelectAll),
    //     take(1)
    //   )
    //   .subscribe((): void => {
    //     const ids: string[] = this.getDisplayedIds();
    //     this.componentState.setSelectedIds({ ids });
    //     this.componentState.updateMultiSelectCheckboxState();
    //   });
  }

  private deselectAllOnPage(): void {
    const ids: (string | number)[] = this.getDisplayedIds();
    this.componentState.removeSelectedIds({ ids });
    this.componentState.turnOffSelectAll();
    this.componentState.updateMultiSelectCheckboxState();
    this.componentState.turnOffFilterBySelected();
    this.params.api.refreshHeader();
  }

  private updateDeviceIdsVisibleOnPage(): void {
    const ids: (string | number)[] = this.getDisplayedIds();
    this.componentState.updateIdsVisibleOnPage({ ids });
    this.componentState.updateMultiSelectCheckboxState();
  }

  private getDisplayedIds(): (string | number)[] {
    const currentPage: number = this.params.api.paginationGetCurrentPage();
    const pageSize: number = this.params.api.paginationGetPageSize();
    const totalRows: number = this.params.api.paginationGetRowCount();

    const firstRow: number = currentPage * pageSize;
    let lastRow: number = (currentPage + 1) * pageSize - 1;
    lastRow = lastRow >= totalRows ? totalRows - 1 : lastRow;

    const ids: (string | number)[] = [];
    for (let i: number = firstRow; i <= lastRow; i += 1) {
      const rowNode: RowNode<T> | undefined =
        this.params.api.getDisplayedRowAtIndex(i);
      if (rowNode?.data?.id) {
        ids.push(rowNode.data.id);
      }
    }
    return ids;
  }
}
